/**
 * @file        Api.service.ts
 * @description Cypher Rest Api service
 * @version     2.0.0
 * @author      Mehran Majidi
 * @copyright   Copyright (c) 2018, MindShare HDV LLC.  All rights reserved.
 *              Property of MindShare HDV LLC.  This file cannot be used or
 *              altered in anyway without expressed written permission from
 *              MindShare HDV LLC.
 */
import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { HttpClient } from "@angular/common/http";
import { map } from "rxjs/operators";

import { SecureModule } from "@app/secure/secure.module";
import { ERROR } from "@app/constants/Error.constant";
import { environment } from "@environments/environment";
import { CyResponse } from "@app/secure/interface/CyResponse.interface";
import { AuthBaseUrl } from
    "@app/secure/helper/AuthBaseUrl.helper";

@Injectable
({
    providedIn: SecureModule
})
class ApiService
{
    //_____________________________ C O N S T R U C T O R ________________________
    /**
     * @desc   - service constructor
     * @access - public
     * @param  - HttpClient http : http request object
     * @return -
     */
    constructor
    (
        private http: HttpClient
    )
    {}


    //______________________ S T A T I C  M E T H O D S ________________________

    //__________________ P R I V A T E  M E T H O D S __________________________

    //___________________ P U B L I C  M E T H O D S ___________________________

    /**
     * @desc   - send a get request
     * @access - public
     * @param  - string apiUrl : api url for the service
     * @return - object : observable with a variable data type
     */
    public get<TResponse = unknown>
    (
        apiUrl: string
    ): Observable<TResponse>
    {
        let serviceObject: Observable<TResponse>;

        serviceObject =
            this.http.get<CyResponse<TResponse>>(
                `${environment.apiUrl}/${apiUrl}`,
                { withCredentials: true }
            )
            .pipe(
            map((cyResponse: CyResponse<TResponse>) =>
            {
                let responseData: TResponse;
                let errorMessage: string;

                if(cyResponse.error === ERROR.NO_ERROR)
                {
                    responseData = cyResponse.cyData!;
                }
                else
                {
                    errorMessage = `API failed: ${apiUrl} ` +
                                   `${cyResponse.error.toString()}`;
                    throw new Error(errorMessage);
                }

                return responseData;
            })
        );

        return serviceObject;
    }

    /**
     * @desc   - send a post request
     * @access - public
     * @param  - string apiUrl : api url for the service
     * @param  - object payload : data to be passed
     *                            Note : the type of variable is dynmaic
     * @return - object : observable with a variable data type
     */
    public post<TResponse = unknown, TBody = unknown>
    (
        apiUrl: string,
        payload?: TBody
    ): Observable<TResponse>
    {
        let serviceObject: Observable<TResponse>;

        serviceObject = this.http.post<CyResponse<TResponse>>(
                            `${environment.apiUrl}/${apiUrl}`,
                            payload,
                            { withCredentials: true })
        .pipe(map((cyResponse: CyResponse<TResponse>) =>
        {
            let responseData: TResponse;
            let errorMessage: string;

            if (cyResponse.error === ERROR.NO_ERROR)
            {
                responseData = cyResponse.cyData ?
                               cyResponse.cyData : cyResponse as TResponse;
            }
            else
            {
                errorMessage = `API failed: ${apiUrl}|` +
                               `${cyResponse.error.toString()}`;
                throw new Error(errorMessage);
            }

            return responseData;
        }));

        return serviceObject;
    }


    /**
     * @desc   - POST to auth base (login/logout/refresh/SSO)
     * @access - public
     * @param  - string apiUrl : relative API path
     * @param  - object payload : request body
     * @return - object : observable cyData or response
     */
    public postAuth<TResponse = unknown, TBody = unknown>
    (
        apiUrl: string,
        payload?: TBody
    ): Observable<TResponse>
    {
        let serviceObject: Observable<TResponse>;
        let baseUrl;

        baseUrl = AuthBaseUrl.resolve();
        serviceObject = this.http.post<CyResponse<TResponse>>(
            `${baseUrl}/${apiUrl}`,
            payload,
            { withCredentials: true })
        .pipe(map((cyResponse: CyResponse<TResponse>) =>
        {
            let responseData: TResponse;
            let errorMessage: string;

            if (cyResponse.error === ERROR.NO_ERROR)
            {
                responseData = cyResponse.cyData ?
                    cyResponse.cyData :
                    cyResponse as TResponse;
            }
            else
            {
                errorMessage = `API failed: ${apiUrl}|` +
                    `${cyResponse.error.toString()}`;
                throw new Error(errorMessage);
            }

            return responseData;
        }));

        return serviceObject;
    }

    /**
     * @desc   - POST to auth base; return raw response body
     * @access - public
     * @param  - string apiUrl : relative API path
     * @param  - object payload : request body
     * @return - object : observable raw cypher response
     */
    public postRawAuth<TResponse = unknown, TBody = unknown>
    (
        apiUrl: string,
        payload?: TBody
    ): Observable<TResponse>
    {
        let serviceObject: Observable<TResponse>;
        let baseUrl;

        baseUrl = AuthBaseUrl.resolve();
        serviceObject = this.http.post<TResponse>(
            `${baseUrl}/${apiUrl}`,
            payload,
            { withCredentials: true }
        );

        return serviceObject;
    }

    /**
     * @desc   - send a post request and return raw CyResponse
     * @access - public
     * @param  - string apiUrl : api url for the service
     * @param  - object payload : data to be passed
     * @return - object : observable raw cypher response
     */
    public postRaw<TResponse = unknown, TBody = unknown>
    (
        apiUrl: string,
        payload?: TBody
    ): Observable<CyResponse<TResponse>>
    {
        let serviceObject: Observable<CyResponse<TResponse>>;

        serviceObject = this.http.post<CyResponse<TResponse>>(
            `${environment.apiUrl}/${apiUrl}`,
            payload,
            { withCredentials: true }
        );

        return serviceObject;
    }

    /**
     * @desc   - send a custom post request, the response is returned and
     *           there is no cyData.  There are custom post that does not
     *           fit Cyher response and can use this custom post
     * @access - public
     * @param  - string apiUrl : api url for the service
     * @param  - object payload : data to be passed
     *                            Note : the type of variable is dynmaic
     * @return - object : observable with a variable data type
     */
    public customPost<T>
    (
        apiUrl: string,
        payload: T
    ): Observable<T>
    {
        const data = JSON.stringify(payload);

        let serviceObject;

        serviceObject = this.http.post(`${environment.apiUrl}/${apiUrl}`,
                                       data,
                                       {
                                           responseType: 'text/csv' as 'json',
                                           withCredentials: true
                                       }
        )
        .pipe(map((response) =>
        {
            return response;
        }));

        return serviceObject;
    }

    /**
     * @desc   - send a custom post for array buffer
     * @access - public
     * @param  - string apiUrl : api url for the service
     * @param  - object payload : data to be passed
     *                            Note : the type of variable is dynmaic
     * @return - object : observable with a variable data type
     */
    public customPostArrayBuffer<T>
    (
        apiUrl: string,
        payload: T
    ): Observable<T>
    {

        let serviceObject;

        serviceObject = this.http.post(
                `${environment.apiUrl}/${apiUrl}`,
                payload,
                {
                    responseType: 'arraybuffer',
                    withCredentials: true
                }
        )
        .pipe(map((response) =>
        {
            return response;

        }));

        return serviceObject;
    }


    /**
     * @desc   - send a custom post for array binary data
     * @access - public
     * @param  - string apiUrl : api url for the service
     * @param  - object payload : data to be passed
     *                            Note : the type of variable is dynmaic
     * @return - object : observable with a variable data type
     */
    public customPostArrayBlob
    (
        apiUrl: string,
        payload: object
    ): Observable<Blob>
    {

        let serviceObject: Observable<Blob>;

        serviceObject = this.http.post(
                `${environment.apiUrl}/${apiUrl}`,
                payload,
                {
                    responseType: 'blob',
                    withCredentials: true
                }
        )
        .pipe(map((response) =>
        {
            return response;

        }));

        return serviceObject;
    }

    /**
     * @desc   - send a post request with FormData for file uploads
     * @access - public
     * @param  - string apiUrl : api url for the service
     * @param  - FormData formData : form data containing file
     * @return - object : observable with a variable data type
     */
    public postFormData<T>
    (
        apiUrl: string,
        formData: FormData
    ): Observable<T>
    {
        let serviceObject;
        let responseData;
        let errorMessage;

        serviceObject = this.http.post<CyResponse<T>>(
                            `${environment.apiUrl}/${apiUrl}`,
                            formData,
                            { withCredentials: true })
        .pipe(map((cyResponse: CyResponse<T>) =>
        {
            if (cyResponse.error === ERROR.NO_ERROR)
            {
                responseData = cyResponse.cyData ?
                               cyResponse.cyData : cyResponse;
            }
            else
            {
                errorMessage = `API failed: ${apiUrl}|` +
                               `${cyResponse.error.toString()}`;
                throw new Error(errorMessage);
            }

            return responseData;
        }));

        return serviceObject;
    }

    /**
     * @desc   - send FormData and return raw CyResponse
     * @access - public
     * @param  - string apiUrl : api url for the service
     * @param  - FormData formData : form data containing file
     * @return - object : observable raw cypher response
     */
    public postRawFormData<T>
    (
        apiUrl: string,
        formData: FormData
    ): Observable<CyResponse<T>>
    {
        let serviceObject;

        serviceObject = this.http.post<CyResponse<T>>(
            `${environment.apiUrl}/${apiUrl}`,
            formData,
            { withCredentials: true }
        );

        return serviceObject;
    }

    /**
     * @desc   - send a get request and return blob
     * @access - public
     * @param  - string fullUrl : full API url
     * @return - object : observable blob response
     */
    public getBlobByUrl
    (
        fullUrl: string
    ): Observable<Blob>
    {
        let serviceObject;

        serviceObject = this.http.get(
            fullUrl,
            {
                responseType: 'blob',
                withCredentials: true
            }
        );

        return serviceObject;
    }
}

export { ApiService }
